import type { AnalysisQuestionRun } from '@aline/common';

import type { DocumentExtractionPause } from '../../../chat-types';

export type QuestionRunProgress = { done: number; total: number; queuedAt?: string };

type QuestionRunCopy = { description: string; title: string };

/**
 * The run's progress while a turn waits on it, for the run card. Null once
 * the wait is over; `progress` is null until the first poll lands.
 */
export function getQuestionRunWaitForBatch(
  pause: DocumentExtractionPause | undefined,
  batchId: string | undefined
): { progress: QuestionRunProgress | null } | null {
  if (!batchId || pause?.phase !== 'midTurn') return null;
  const entry = pause.pauses.find(
    candidate => candidate.kind === 'questionRun' && candidate.batchId === batchId
  );
  if (entry?.kind !== 'questionRun') return null;
  return { progress: entry.progress ?? null };
}

/**
 * Progress the wait poller last wrote for this analysis card. Null when the
 * card is not waiting on a run, or the first poll has not landed yet.
 */
export function getQuestionRunProgress(
  pause: DocumentExtractionPause | undefined,
  toolCallId: string
): QuestionRunProgress | null {
  return findQuestionRunPause(pause, toolCallId)?.progress ?? null;
}

/** The batch this analysis card is waiting on, for the Stop run button. */
export function getQuestionRunBatchId(
  pause: DocumentExtractionPause | undefined,
  toolCallId: string
): string | null {
  return findQuestionRunPause(pause, toolCallId)?.batchId ?? null;
}

/** The card while the analysis waits for an approved run to finish. */
export function getQuestionRunWaitCopy({
  progress,
  questionRun,
}: {
  progress: QuestionRunProgress | null;
  questionRun: AnalysisQuestionRun;
}): QuestionRunCopy {
  const total = progress?.total ?? questionRun.total;
  return {
    description: `Running ${formatQuestionTitles(questionRun.questionTitles)} on ${formatDocumentCount(total)}. Aline answers once the run finishes.`,
    title: 'Waiting for extraction questions...',
  };
}

/** The card when the turn ended while it was still waiting, e.g. a cancelled turn. */
export const INTERRUPTED_QUESTION_RUN_COPY: QuestionRunCopy = {
  description: 'The turn ended before the run finished, so Aline did not answer.',
  title: 'Stopped waiting for extraction questions',
};

/** One line above a finished answer that waited on a run. Null when it did not wait. */
export function describeFinishedQuestionRun(
  questionRun: AnalysisQuestionRun | undefined
): string | null {
  if (questionRun?.status !== 'finished') return null;
  const titles = formatQuestionTitles(questionRun.questionTitles);
  switch (questionRun.outcome) {
    case 'partial':
      return `Ran ${titles} first. Some documents did not finish, so the answer leaves them out.`;
    case 'stopped':
      return `The run of ${titles} was stopped, so the answer uses the documents that had finished.`;
    case 'timedOut':
      return `The run of ${titles} took too long, so the answer uses the documents that had finished.`;
    case 'ready':
    case undefined:
      return `Ran ${titles} on ${formatDocumentCount(questionRun.total)} first.`;
  }
}

function findQuestionRunPause(pause: DocumentExtractionPause | undefined, toolCallId: string) {
  if (pause?.phase !== 'midTurn') return null;
  const entry = pause.pauses.find(
    candidate => candidate.kind === 'questionRun' && candidate.toolCallId === toolCallId
  );
  return entry?.kind === 'questionRun' ? entry : null;
}

function formatQuestionTitles(titles: string[]) {
  if (!titles.length) return 'the missing questions';
  const quoted = titles.map(title => `"${title}"`);
  if (quoted.length === 1) return quoted[0];
  return `${quoted.slice(0, -1).join(', ')} and ${quoted[quoted.length - 1]}`;
}

function formatDocumentCount(count: number) {
  return `${count} ${count === 1 ? 'document' : 'documents'}`;
}
